import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useMachines } from "@/hooks/queries/use-mailbox";
import { useAiSettings, useStartKeyInterview } from "@/hooks/queries/use-ai-settings";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import { AiSetupStep } from "@/components/onboarding/ai-setup-step";
import { RunOnMachineDialog } from "./run-on-machine-dialog";
import { keyCanRun } from "./run-with-choice";

/**
 * "New Request": starts the request interview the way this person's AI is set
 * up — on their paired computer, or on CompBox with their API key (their own,
 * or the company key they inherit). With neither, it shows the same "How will
 * AI do your work?" step as signup, so someone who skipped it there picks one
 * now; adding a key there goes straight into the interview.
 */
export function NewRequestButton({ children }: { children: React.ReactNode }) {
  const { data: machines, isLoading: machinesLoading } = useMachines();
  const { data: settings, isLoading: settingsLoading } = useAiSettings();

  const hasComputer = (machines ?? []).length > 0;

  if (machinesLoading || settingsLoading) {
    return (
      <Button disabled>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        {children}
      </Button>
    );
  }

  if (hasComputer || keyCanRun(settings, "interview")) {
    return <RunOnMachineDialog>{children}</RunOnMachineDialog>;
  }

  return <SetUpAiDialog isAdmin={settings?.canManageCompanyKey ?? false}>{children}</SetUpAiDialog>;
}

function SetUpAiDialog({ children, isAdmin }: { children: React.ReactNode; isAdmin: boolean }) {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const { data: onboarding } = useOnboardingState();
  const startKeyInterview = useStartKeyInterview();
  const organizationName =
    onboarding && "organizationName" in onboarding && onboarding.organizationName
      ? onboarding.organizationName
      : "your organization";

  const startOnKey = async () => {
    const response = await startKeyInterview.mutateAsync({});
    setOpen(false);
    void navigate(`/mailbox/jobs/${response.data.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>{children}</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogTitle className="sr-only">Set up AI to start a request</DialogTitle>
        {startKeyInterview.isPending ? (
          <p className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Starting your interview…
          </p>
        ) : (
          <AiSetupStep
            organizationName={organizationName}
            isAdmin={isAdmin}
            onSkip={() => { setOpen(false); }}
            onKeySaved={() => void startOnKey().catch(() => undefined)}
          />
        )}
        {startKeyInterview.error ? (
          <p className="text-center text-sm text-red-400" role="alert">
            {startKeyInterview.error instanceof Error ? startKeyInterview.error.message : "Couldn't start the interview."}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
